import React, { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Phone, Mail, MapPin, Send, Clock, ArrowUpRight, Check } from "lucide-react";
import { CONTACT } from "./siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];
const FOIL = "linear-gradient(100deg,#8B6B3A 0%,#E9D3A1 25%,#B89B72 45%,#F4E4BC 60%,#8B6B3A 100%)";
const SW = [
  "repeating-linear-gradient(45deg,#1f5f78 0 14px,#a8382f 14px 28px,#c98f2e 28px 42px)",
  "radial-gradient(circle at 12px 12px,#f2c14e 0 4px,transparent 5px) 0 0/24px 24px,#24406a",
  "repeating-linear-gradient(90deg,#c97bb2 0 12px,#b25f9a 12px 24px)",
  "conic-gradient(from 45deg,#b5543a 25%,#f0e0c8 0 50%,#b5543a 0 75%,#f0e0c8 0) 0 0/26px 26px",
];
const zig = (n = 10, d = 6) => {
  const p = ["0 0", "100% 0"];
  for (let i = 1; i <= n; i++) p.push(`${i % 2 ? `calc(100% - ${d}px)` : "100%"} ${(i * 100) / n}%`);
  p.push("0 100%");
  for (let i = n - 1; i >= 0; i--) p.push(`${i % 2 ? `${d}px` : "0"} ${(i * 100) / n}%`);
  return `polygon(${p.join(",")})`;
};
const awningClip = (() => {
  const p = ["0 0", "100% 0"]; const n = 60;
  for (let i = n; i >= 0; i--) p.push(`${(i * 100) / n}% ${i % 2 ? "calc(100% - 9px)" : "100%"}`);
  return `polygon(${p.join(",")})`;
})();

const PRODUCTS = ["Kurti fabric", "Dress fabric", "Event backdrop", "Custom print"];
const CONFETTI = [[6, 13, 46, 0], [90, 10, 38, 1], [14, 37, 26, 2], [84, 36, 30, 3], [4, 58, 22, 1], [94, 60, 26, 0], [44, 4, 18, 2], [57, 40, 16, 3]];

const CSS = `
.nc-linen{background-image:repeating-linear-gradient(0deg,rgba(120,90,50,.035) 0 1px,transparent 1px 3px),repeating-linear-gradient(90deg,rgba(120,90,50,.035) 0 1px,transparent 1px 3px)}
@keyframes nc-sway{0%{transform:rotate(calc(var(--r) - 3deg))}100%{transform:rotate(calc(var(--r) + 3deg))}}
.nc-tw{transform-origin:50% 0;animation:nc-sway 2.8s ease-in-out infinite alternate;animation-delay:var(--d)}
.nc-tw:hover{animation-play-state:paused}
@keyframes nc-shim{0%{background-position:0% 50%}100%{background-position:200% 50%}}
.nc-foil{background:${FOIL};background-size:200% 100%;animation:nc-shim 3.5s linear infinite}
.nc-foiltext{background:${FOIL};background-size:200% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;animation:nc-shim 3s linear infinite}
@keyframes nc-flap{0%,100%{transform:scaleY(1)}50%{transform:scaleY(1.12)}}
.nc-awn>div{transform-origin:top;animation:nc-flap 2.4s ease-in-out infinite}
@keyframes nc-spin{to{transform:rotate(360deg)}}
.nc-spool{position:absolute;top:-16px;width:40px;height:40px;border-radius:50%;box-shadow:0 0 0 5px #8B6B3A,0 4px 10px rgba(0,0,0,.25);animation:nc-spin 3s linear infinite}
.nc-spool:after{content:"";position:absolute;inset:14px;border-radius:50%;background:#FBF3E4}
@keyframes nc-float{0%,100%{transform:translateY(0) rotate(0)}50%{transform:translateY(-22px) rotate(25deg)}}
.nc-float{animation:nc-float 5s ease-in-out infinite}
.nc-tag{clip-path:polygon(18% 0,82% 0,100% 14%,100% 100%,0 100%,0 14%);filter:drop-shadow(0 8px 14px rgba(60,40,20,.12));transition:transform .35s}
.nc-tag:hover{transform:translateY(-8px) scale(1.04)}
.nc-tag:before{content:"";position:absolute;inset:8px;border:1px dashed rgba(156,123,76,.45);clip-path:inherit;pointer-events:none}
.nc-stitch rect{fill:none;stroke:#B89B72;stroke-width:1.6;stroke-dasharray:9 7;animation:nc-march 1.6s linear infinite}
@keyframes nc-march{to{stroke-dashoffset:-32}}
@keyframes nc-sew{0%{left:8%}100%{left:86%}}
.nc-needle{position:absolute;top:2px;width:70px;height:20px;animation:nc-sew 5s ease-in-out infinite alternate;z-index:3}
.nc-ruler{background:repeating-linear-gradient(90deg,#C9A96E 0 1px,transparent 1px 10px),repeating-linear-gradient(90deg,#9C7B4C 0 1px,transparent 1px 50px);background-size:auto 7px,auto 14px;background-repeat:repeat-x;opacity:.6}
.nc-in{width:100%;background:transparent;border:0;border-bottom:1.5px dotted #c9b48f;padding:10px 2px;font-size:14px;outline:none;transition:border-color .3s}
.nc-in:focus{border-bottom:1.5px solid #9C7B4C}
.nc-in::placeholder{color:#b3a896}
.nc-ta{border:1.5px dashed #d8c7a6;border-radius:4px;padding:12px;background:repeating-linear-gradient(0deg,transparent 0 27px,#efe5d2 27px 28px);background-attachment:local;line-height:28px}
.nc-ta:focus{border-color:#9C7B4C}
@keyframes nc-pop{0%,100%{transform:scale(1)}50%{transform:scale(1.07)}}
.nc-chip-on{animation:nc-pop 1.8s ease-in-out infinite;box-shadow:0 0 0 4px rgba(201,169,110,.18)}
@keyframes nc-shine{0%{left:-60%}60%,100%{left:130%}}
.nc-btn{overflow:hidden}
.nc-btn:before{content:"";position:absolute;top:0;bottom:0;width:40%;background:linear-gradient(100deg,transparent,rgba(255,255,255,.75),transparent);transform:skewX(-20deg);animation:nc-shine 2.6s ease-in-out infinite}
.nc-btn:after{content:"";position:absolute;inset:5px;border:1px dashed rgba(26,20,16,.35);pointer-events:none}
@keyframes nc-sl{0%{background-position:-100% 0}100%{background-position:200% 0}}
.nc-bar{height:2px;background:linear-gradient(90deg,transparent,#E9D3A1,transparent);background-size:50% 100%;background-repeat:no-repeat;animation:nc-sl 2.6s linear infinite}
@keyframes nc-wob{0%,100%{transform:rotate(1.6deg)}50%{transform:rotate(-.6deg)}}
.nc-pol{animation:nc-wob 4s ease-in-out infinite;transform-origin:50% 0}
@keyframes nc-marq{to{transform:translateX(-50%)}}
.nc-marq{animation:nc-marq 18s linear infinite}
`;

function Swatch({ i, children }) {
  return (
    <div className="mx-auto mt-3 grid h-[54px] w-[54px] place-items-center rounded-full" style={{ background: SW[i % 4], boxShadow: "inset 0 0 0 3px #fff,0 0 0 1px rgba(0,0,0,.08)" }}>
      <span className="grid h-9 w-9 place-items-center rounded-full bg-white text-[#1A1410]">{children}</span>
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", product: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const tags = [
    { icon: MessageCircle, title: "WhatsApp", sub: "Chat with our team", val: CONTACT.phone, href: `https://wa.me/${CONTACT.whatsapp}`, ext: true },
    { icon: Phone, title: "Call us", sub: "Mon–Sat · 9am–7pm", val: CONTACT.phone, href: `tel:${CONTACT.phone}` },
    { icon: Mail, title: "Email", sub: "We reply within a day", val: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: MapPin, title: "Visit Factory", sub: "Our Tiruppur studio", val: "Kumar Nagar, Tiruppur", href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CONTACT.address || "Kumar Nagar Tiruppur")}`, ext: true },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setStatus("sending");
    try {
      const payload = { ...form, product_interest: form.product, source: "website-contact" };
      const res = await fetch(`${API}/public/enquiry`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await res.text());
      setStatus("done");
      setForm({ name: "", company: "", phone: "", email: "", product: "", message: "" });
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const mapQ = encodeURIComponent(CONTACT.address || "Kumar Nagar, Tiruppur");

  return (
    <div data-testid="contact-page" className="relative overflow-hidden bg-[#FBF8F2] text-[#1A1A1A]">
      <style>{CSS}</style>
      <div className="nc-linen pointer-events-none absolute inset-0" />

      {/* Awning */}
      <div className="nc-awn relative z-10 flex h-[58px]" style={{ clipPath: awningClip, filter: "drop-shadow(0 4px 6px rgba(0,0,0,.12))" }}>
        {Array.from({ length: 24 }, (_, i) => (
          <div key={i} className="flex-1" style={{ background: i % 2 ? "#FBF3E4" : "#1A1410", animationDelay: `${-i * 0.1}s` }} />
        ))}
      </div>

      {/* Hero */}
      <section className="relative px-6 pb-8 pt-14 text-center">
        {CONFETTI.map((c, i) => (
          <span key={i} className="nc-float pointer-events-none absolute hidden rounded-full opacity-50 sm:block"
            style={{ left: `${c[0]}%`, top: `${c[1]}%`, width: c[2], height: c[2], background: SW[c[3]], animationDelay: `${-i * 0.8}s` }} />
        ))}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
          <div className="inline-flex items-center gap-3.5 text-[11px] uppercase tracking-[0.34em] text-[#9C7B4C]">
            <span className="h-px w-9 bg-[#B89B72]" />Contact the Studio<span className="h-px w-9 bg-[#B89B72]" />
          </div>
          <h1 className="mt-5 font-display text-5xl font-light leading-none tracking-tight sm:text-7xl lg:text-[84px]">
            Let's craft something <em className="nc-foiltext pr-1.5">beautiful.</em>
          </h1>
          <p className="mx-auto mt-5 max-w-[520px] text-[15px] leading-relaxed text-[#6b6157]">
            {CONTACT.tagline || "Pick a thread below — chat, call, write or walk into our Tiruppur studio. Every enquiry gets a personal reply."}
          </p>
        </motion.div>

        {/* Hanging tags rail */}
        <div className="relative mx-auto mt-12 max-w-[1180px]">
          <div className="nc-foil absolute left-0 right-0 top-[14px] hidden h-2 rounded-md shadow md:block">
            <span className="nc-spool -left-[30px]" style={{ background: "repeating-conic-gradient(#a8382f 0 20deg,#c98f2e 20deg 40deg)" }} />
            <span className="nc-spool -right-[30px]" style={{ background: "repeating-conic-gradient(#1f5f78 0 20deg,#c97bb2 20deg 40deg)", animationDirection: "reverse" }} />
          </div>
          <div className="grid grid-cols-2 gap-4 md:flex md:justify-around md:gap-0">
            {tags.map((t, i) => {
              const Icon = t.icon;
              return (
                <motion.div key={t.title} initial={{ y: -160, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 120, damping: 9, delay: 0.3 + i * 0.18 }}>
                  <div className="nc-tw flex flex-col items-center" style={{ "--r": `${[-2.5, 1.8, -1.2, 2.4][i]}deg`, "--d": `${-i * 0.7}s` }}>
                    <span className="mt-[18px] hidden h-[46px] w-px bg-[#9C7B4C] md:block" />
                    <a href={t.href} target={t.ext ? "_blank" : undefined} rel="noreferrer" data-testid={`contact-tag-${i}`}
                      className="nc-tag relative block w-full rounded-t-md bg-white px-5 pb-6 pt-8 text-center md:w-[230px]">
                      <span className="absolute left-1/2 top-2.5 -ml-1.5 h-3 w-3 rounded-full border-2 border-[#C9A96E] bg-[#FBF8F2]" />
                      <Swatch i={i}><Icon className="h-[18px] w-[18px]" strokeWidth={1.5} /></Swatch>
                      <h3 className="mt-3 font-display text-[26px] font-light">{t.title}</h3>
                      <p className="mt-1 text-xs text-[#8a7f72]">{t.sub}</p>
                      <b className="mt-2.5 block break-all text-[13px] font-normal tracking-wide">{t.val}</b>
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Order slip + side */}
      <section className="relative mx-auto grid max-w-[1180px] gap-12 px-6 pb-24 pt-14 lg:grid-cols-[1fr_430px]">
        <motion.form onSubmit={submit} data-testid="contact-form"
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease: EASE }}
          className="relative bg-[#FFFDF8] px-7 pb-11 pt-12 shadow-[0_18px_40px_rgba(60,40,20,.1)] sm:px-12" style={{ clipPath: zig(40, 7) }}>
          <svg className="nc-stitch pointer-events-none absolute inset-[14px] h-[calc(100%-28px)] w-[calc(100%-28px)] overflow-visible"><rect x="0" y="0" width="100%" height="100%" rx="2" /></svg>
          <svg className="nc-needle" viewBox="0 0 70 20"><path d="M0 14 Q14 2 26 10" stroke="#a8382f" strokeWidth="1.4" fill="none" strokeDasharray="3 2" /><path d="M26 10 L66 9 L70 10 L66 11 L26 12 Z" fill="#8f8f8f" /><ellipse cx="31" cy="10.5" rx="3" ry="0.9" fill="#fff" /></svg>
          <div className="absolute left-1/2 top-[-10px] h-[30px] w-[150px] -ml-[75px] -rotate-2" style={{ background: "repeating-linear-gradient(45deg,rgba(201,169,110,.55) 0 8px,rgba(233,211,161,.55) 8px 16px)" }} />

          <div className="relative flex items-end justify-between gap-4 border-b border-[#eadfcb] pb-4">
            <div>
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#9C7B4C]"><span className="h-px w-9 bg-[#B89B72]" />Order Slip</div>
              <h2 className="mt-2 font-display text-3xl font-light sm:text-[42px] sm:leading-tight">Tell us what you're <em>stitching up</em></h2>
            </div>
            <div className="hidden text-right text-[11px] uppercase leading-[1.8] tracking-[0.24em] text-[#9C7B4C] sm:block">No. 0247<br />Nayara Fab Studio</div>
          </div>
          <div className="nc-ruler relative mb-2 mt-4 h-3.5" />

          <div className="relative mt-5 grid gap-x-7 gap-y-6 sm:grid-cols-2">
            {[["name", "Name *", "Your full name", "text"], ["company", "Company", "Brand / boutique", "text"], ["phone", "Phone", "+91", "tel"], ["email", "Email", "you@email.com", "email"]].map(([k, l, ph, type]) => (
              <label key={k} className="flex flex-col">
                <span className="text-[10.5px] uppercase tracking-[0.24em] text-[#9C7B4C]">{l}</span>
                <input className="nc-in mt-1" type={type} placeholder={ph} value={form[k]} onChange={set(k)} required={k === "name"} data-testid={`contact-${k}`} />
              </label>
            ))}
            <div className="sm:col-span-2">
              <span className="text-[10.5px] uppercase tracking-[0.24em] text-[#9C7B4C]">Product of interest</span>
              <div className="mt-3 flex flex-wrap gap-2.5">
                {PRODUCTS.map((p, i) => {
                  const on = form.product === p;
                  return (
                    <button type="button" key={p} onClick={() => setForm((f) => ({ ...f, product: on ? "" : p }))}
                      className={`flex items-center gap-2 rounded-full border py-1.5 pl-2 pr-3.5 text-[12.5px] transition-colors ${on ? "nc-chip-on border-[#9C7B4C] bg-[#f6eedf] text-[#1A1A1A]" : "border-[#e3d5bb] bg-white text-[#5b5147] hover:border-[#B89B72]"}`}>
                      <span className="h-[18px] w-[18px] rounded-full" style={{ background: SW[[0, 2, 1, 3][i]] }} />{p}{on && <Check className="h-3.5 w-3.5 text-[#9C7B4C]" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="flex flex-col sm:col-span-2">
              <span className="text-[10.5px] uppercase tracking-[0.24em] text-[#9C7B4C]">Message</span>
              <textarea rows={4} className="nc-ta mt-2.5 resize-none text-sm outline-none placeholder:text-[#b3a896]" placeholder="Quantity, fabric, colours, deadline…" value={form.message} onChange={set("message")} data-testid="contact-message" />
            </label>
          </div>

          <div className="relative mt-8 flex flex-wrap items-center justify-between gap-4">
            <button type="submit" disabled={status === "sending"} data-testid="contact-submit"
              className="nc-btn nc-foil relative inline-flex items-center gap-3 py-[17px] pl-[50px] pr-10 text-[13px] uppercase tracking-[0.14em] text-[#1A1410] transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ clipPath: zig(10, 6) }}>
              <span className="absolute left-[18px] top-1/2 -mt-[4.5px] h-[9px] w-[9px] rounded-full bg-[#1A1410]" />
              {status === "sending" ? "Sending…" : "Send Enquiry"} <Send className="h-[15px] w-[15px]" strokeWidth={1.5} />
            </button>
            {status === "done" ? <span className="text-sm text-[#2f6b4f]">✦ Thank you! We'll get back to you shortly.</span>
              : status === "error" ? <span className="text-sm text-[#a8382f]">Couldn't send — please WhatsApp us instead.</span>
              : <span className="flex items-center gap-2 text-xs text-[#8a7f72]"><Clock className="h-[15px] w-[15px] text-[#9C7B4C]" strokeWidth={1.5} />Usually replies within 2 hours</span>}
          </div>
        </motion.form>

        <div className="flex flex-col gap-8">
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="relative bg-[#1A1410] p-8 text-[#FAF9F6] sm:p-9" style={{ clipPath: zig(16, 6) }}>
            <div className="pointer-events-none absolute inset-[10px] border border-dashed border-[#C9A96E]/50" />
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#C9A96E]">Woven Label</div>
            <h4 className="mt-2 font-display text-[34px] font-light">Nayara <em className="nc-foiltext">Fab Studio</em></h4>
            <div className="nc-bar mt-3.5" />
            {[[Phone, "Call", CONTACT.phone, `tel:${CONTACT.phone}`], [MessageCircle, "WhatsApp", CONTACT.phone, `https://wa.me/${CONTACT.whatsapp}`], [Mail, "Email", CONTACT.email, `mailto:${CONTACT.email}`], [MapPin, "Visit the studio", CONTACT.address, null]].map(([Icon, l, v, href]) => {
              const inner = (<><span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-full border border-[#C9A96E]/50 transition-colors group-hover:bg-[#C9A96E]/15"><Icon className="h-[15px] w-[15px] text-[#C9A96E]" strokeWidth={1.5} /></span>
                <div><small className="block text-[10px] uppercase tracking-[0.24em] text-[#C9A96E]">{l}</small>{v}</div></>);
              return href
                ? <a key={l} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="group relative mt-[18px] flex items-start gap-3.5 text-[13.5px] leading-relaxed text-white/80 hover:text-white">{inner}</a>
                : <div key={l} className="group relative mt-[18px] flex items-start gap-3.5 text-[13.5px] leading-relaxed text-white/80">{inner}</div>;
            })}
            <div className="relative mt-6 border-t border-dashed border-[#C9A96E]/35 pt-5 text-[12.5px] leading-relaxed text-white/60">
              Service coverage — Pan-India dispatch for boutiques, brands & event decorators.
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}>
            <div className="nc-pol relative bg-white p-3.5 pb-12 shadow-[0_16px_34px_rgba(60,40,20,.16)]">
              <span className="absolute -left-4 -top-3 z-10 h-[26px] w-[90px] -rotate-[28deg] opacity-85" style={{ background: SW[2] }} />
              <span className="absolute -right-4 -top-3 z-10 h-[26px] w-[90px] rotate-[30deg] opacity-85" style={{ background: SW[0] }} />
              <div className="relative h-[240px] overflow-hidden">
                <iframe title="Nayara location" src={`https://www.google.com/maps?q=${mapQ}&output=embed`} className="h-full w-full border-0 grayscale-[30%] sepia-[15%]" loading="lazy" />
                <a href={`https://www.google.com/maps/search/?api=1&query=${mapQ}`} target="_blank" rel="noreferrer"
                  className="absolute right-3 top-3 flex items-center gap-1.5 bg-white px-3 py-1.5 text-[11.5px] shadow hover:bg-[#f6eedf]">Open in Maps <ArrowUpRight className="h-3.5 w-3.5" /></a>
              </div>
              <div className="absolute inset-x-0 bottom-3 text-center font-display text-[21px] font-light italic text-[#5b5147]">Find us in Tiruppur ✦</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ribbon */}
      <div className="relative flex h-14 items-center overflow-hidden bg-[#1A1410]">
        <div className="nc-marq flex whitespace-nowrap font-display text-[22px] font-light italic text-[#C9A96E]">
          {Array.from({ length: 12 }, (_, i) => (
            <span key={i} className="flex items-center gap-10 pl-10">Made with love in Tiruppur<span className="inline-block h-[22px] w-[22px] rounded-full" style={{ background: SW[i % 4] }} /></span>
          ))}
        </div>
      </div>
    </div>
  );
}
